import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  FormControl,
  CircularProgress,
  Typography,
} from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import {
  getEmployeeId,
  getUserData,
  s3Client,
} from "../../../Base/helper/baseFunctions";
import {
  GetDailyWageReportAction,
  GetWageReportAction,
} from "../../../../../store/Finance/Reports/Action";

import {
  GetDailyWagesEmployeePaySlipHelper,
  GetEmployeePaySlipHelper,
} from "../../helper/PaySlipHelper";
import NewPaySlip from "../Employee/NewPaySlip";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import { toast } from "react-toastify";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { GetObjectCommand } from "@aws-sdk/client-s3";

const MyPaySlipReport = () => {
  const dispatch = useDispatch();
  const userData = getUserData();
  const date = new Date();
  const [filters, setFilters] = useState({
    month_year: `${date.getFullYear()}-${
      date.getMonth() + 1
    }-${date.getDate()}`,
  });
  const selectedDate = new Date(filters.month_year);
  const [name, setName] = useState(
    selectedDate.toLocaleString("default", { month: "short", year: "numeric" })
  );
  const { letterHeadData } = useSelector(
    (state) => state?.AncillaryReducer?.LetterHeadReducer
  );
  const [pdfUrl, setPdfUrl] = useState("");
  const [previewFileName, setPreviewFileName] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading1, setIsLoading1] = useState(false);
  const { company_logo } = useSelector(state => state?.AncillaryReducer?.LetterHeadReducer);

  useEffect(() => {
    const callPdfGeneration = async () => {
      if (filters.month_year) {
        setIsLoading1(true);
        setMessage("");
        const [f_year, month] = filters?.month_year?.split("-");
        const f_month = parseInt(month, 10).toString();
        if (userData?.payroll_daily_wages_template) {
          dispatch(
            GetDailyWageReportAction(
              {
                month: f_month,
                year: f_year,
                employee_id: `{${getEmployeeId()}}`,
              },
              (error, result) => {
                if (!error && result?.data?.statuscode === 200) {
                  if (result?.data?.data?.length === 0) {
                    setMessage(
                      "Pay summary not generated for this month-year selection."
                    );
                    setPdfUrl("");
                    setPreviewFileName("");
                    setIsLoading1(false);
                  } else {
                    handleViewPaySlip();
                  }
                } else {
                  toast.dismiss();
                  setIsLoading1(false);
                  toast.error("Something went wrong");
                }
              }
            )
          );
        } else {
          dispatch(
            GetWageReportAction(
              {
                month: f_month,
                year: f_year,
                employee_id: `{${getEmployeeId()}}`,
              },
              (error, result) => {
                if (!error && result?.data?.statuscode === 200) {
                  if (result?.data?.data?.length === 0) {
                    setMessage(
                      "Pay summary not generated for this month-year selection."
                    );
                    setIsLoading1(false);
                    setPdfUrl("");
                    setPreviewFileName("");
                  } else {
                    handleViewPaySlip();
                  }
                } else {
                  toast.dismiss();
                  setIsLoading1(false);
                  toast.error("Something went wrong");
                }
              }
            )
          );
        }
      }

    }
    callPdfGeneration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.month_year]);

  const handleFilter = (event) => {
    if (["month_year"].includes(event?.target?.name)) {
      const updatedFilters = {
        ...filters,
        [event?.target?.name]: event?.target?.value,
      };
      setFilters(updatedFilters);
      const selectedDate = new Date(updatedFilters.month_year);
      const formattedName = selectedDate.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      setName(formattedName);
    }
  };

  const handleViewPaySlip = async (rowData) => {
    const [f_year, month] = filters.month_year.split("-");
    const f_month = parseInt(month, 10).toString();

    try {
      let res;
      if (userData?.payroll_daily_wages_template) {
        res = await GetDailyWagesEmployeePaySlipHelper({
          employee_id: userData?.employee_id,
          month: f_month,
          year: f_year,
        });
      } else {
        res = await GetEmployeePaySlipHelper({
          employee_id: userData?.employee_id,
          month: f_month,
          year: f_year,
        });
      }
      if (res?.data?.data?.[0]) {
        const imageUl = await getImageSignedUrl(company_logo);
        const MyDocument = (
          <NewPaySlip
            data={res.data.data[0]}
            imageUrl={imageUl}
            letterHeadData={letterHeadData?.[0]}
          />
        );
        const blob = await pdf(MyDocument).toBlob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPreviewFileName(`${userData?.employee_no} - ${name}.pdf`);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.dismiss();
      toast.error("Something went wrong");
    } finally {
      setIsLoading1(false);
    }
  };

  
  const getImageSignedUrl = async (currentData) => {
    let filename = currentData;
    const bucketParams = {
      Bucket: process.env.REACT_APP_DO_BUCKET_NAME,
      Key: filename,
    };
    const expiresIn = 604800;
    try {
      const url = await getSignedUrl(
        s3Client,
        new GetObjectCommand(bucketParams),
        { expiresIn }
      ); // Adjustable expiration.
      return url;
    } catch (err) {
      console.error("Error fetching image URL", err);
    }
  };


  return (
    <>
      <Box sx={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto"}}>
        <Box sx={{ m: 1 }}>
          <Grid
            sx={{ display: "flex", justifyContent: "space-between" }}
            container
            columnSpacing={1}
          >
            <FormControl>
              <CommonDatePicker
                name="month_year"
                openTo="month"
                views={["year", "month"]}
                label="Month and Year"
                pickerType="date"
                size="small"
                onKeyDown={(e) => e.preventDefault()}
                onKeyPress={(e) => e.preventDefault()}
                pickerFormat="MM/yyyy"
                DefaultDTvalue={filters.month_year}
                maxDate={new Date()}
                minDate={new Date(userData?.doj)}
                handleChange={(value) =>
                  handleFilter({
                    target: {
                      name: "month_year",
                      value: value
                        ? `${value?.getFullYear()}-${(
                            "0" +
                            (value?.getMonth() + 1)
                          ).slice(-2)}-${("0" + value?.getDate()).slice(-2)}`
                        : "",
                    },
                  })
                }
              />
            </FormControl>
           { (pdfUrl && !isLoading1) &&  <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                pr: 1,
                mb: -2.5
              }}
            >
              <a href={pdfUrl} download={previewFileName}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CloudDownloadOutlinedIcon sx={{ cursor: "pointer" }} />
                  <span>Download</span>
                </Box>
              </a>
            </Box>}
          </Grid>
        </Box>

        {pdfUrl ? (
          <Box
            height="calc(100vh - 190px)"
            position={"relative"}
            overflow={"hidden"}
          >
            {isLoading1 && (
              <Box
               sx={loaderOverlayStyle}>
                <CircularProgress disableShrink color="primary" />
              </Box>
            )}
            <iframe
              src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title="Pay Slip Preview"
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 4,
              }}
            ></iframe>
          </Box>
        ) : (
          <Box
            style={messageBoxStyle}
          >
            <Typography sx={{ color: "grey.500", fontWeight: "bold" }}>
            {message}
            </Typography>
            {isLoading1 && (
              <Box
                sx={loaderOverlayStyle}>
                <CircularProgress disableShrink color="primary" />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
const loaderOverlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const messageBoxStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "calc(100vh - 195px)",
    position: "relative",
    border: "1px solid #ddd",
    borderRadius: 5,
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
    padding: 2,
    mx: 2, 
};
export default MyPaySlipReport;
